import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAction, useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { api } from "../convex/_generated/api";
import { Id } from "../convex/_generated/dataModel";

interface User {
  _id: Id<"users">;
  username: string;
  email: string;
  role: "user" | "admin";
  profileImageUrl?: string;
}

interface AuthContextType {
  user: User | null;
  /** A szerver által kiadott munkamenet-token — minden védett Convex hívásnak ezt kell átadni. */
  sessionToken: string | null;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  register: (
    username: string,
    email: string,
    password: string
  ) => Promise<boolean>;
  updateProfile: (userData: Partial<User> | FormData) => Promise<boolean>;
  checkAuth: () => Promise<boolean>;
  clearError: () => void;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const SESSION_TOKEN_KEY = "kertmagad_session";
const LEGACY_USER_ID_KEY = "convex_userId";

/** Mint a useAuth, de AuthProvider nélkül nem dob hibát (undefined-ot ad). */
export const useOptionalAuth = (): AuthContextType | undefined => useContext(AuthContext);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Munkamenet-token (a szerveren csak a hash-e van). A régi, kliensoldali
  // userId-s belépés (convex_userId) érvénytelen — onnan újra be kell lépni.
  const [sessionToken, setSessionToken] = useState<string | null>(() => {
    localStorage.removeItem(LEGACY_USER_ID_KEY);
    return localStorage.getItem(SESSION_TOKEN_KEY);
  });
  const navigate = useNavigate();

  const loginAction = useAction(api.passwordActions.loginUser);
  const registerAction = useAction(api.passwordActions.registerUser);
  const updateProfileFullAction = useAction(api.passwordActions.updateProfileFull);
  const logoutMutation = useMutation(api.sessions.logout);
  const generateProfileImageUploadUrl = useMutation(api.users.generateProfileImageUploadUrl);
  const setProfileImageMutation = useMutation(api.users.setProfileImage);

  const currentUser = useQuery(
    api.users.getProfile,
    sessionToken ? { sessionToken } : "skip"
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    return !!user;
  }, [user]);

  useEffect(() => {
    if (!sessionToken) {
      setUser(null);
      setLoading(false);
      return;
    }

    if (currentUser === undefined) {
      setLoading(true);
    } else if (currentUser === null) {
      setUser(null);
      setLoading(false);
      // Lejárt vagy visszavont munkamenet
      localStorage.removeItem(SESSION_TOKEN_KEY);
      setSessionToken(null);
    } else {
      setUser({
        _id: currentUser.userId as Id<"users">,
        username: currentUser.username,
        email: currentUser.email,
        role: currentUser.role,
        profileImageUrl: currentUser.profileImageUrl,
      });
      setLoading(false);
    }
  }, [currentUser, sessionToken]);

  const login = useCallback(
    async (email: string, password: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await loginAction({ email, password });

        if (result) {
          localStorage.setItem(SESSION_TOKEN_KEY, result.sessionToken);
          setSessionToken(result.sessionToken);
          return true;
        } else {
          setError("Sikertelen bejelentkezés");
          return false;
        }
      } catch (err: any) {
        console.error("Login error:", err);
        const errorMessage =
          err instanceof ConvexError
            ? (typeof err.data === "string" ? err.data : (err.data as any)?.message || "Hiba történt")
            : err.message || "Hiba történt a bejelentkezés során";
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [loginAction]
  );

  const logout = useCallback(async () => {
    const token = sessionToken;
    localStorage.removeItem(SESSION_TOKEN_KEY);
    setSessionToken(null);
    setUser(null);
    setError(null);
    if (token) {
      // A szerveren is érvénytelenítjük; ha nem sikerül, a token 30 nap múlva lejár
      logoutMutation({ sessionToken: token }).catch((err) => console.error("Logout error:", err));
    }
    navigate("/login");
  }, [navigate, sessionToken, logoutMutation]);

  const register = useCallback(
    async (
      username: string,
      email: string,
      password: string
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await registerAction({ username, email, password });

        if (result) {
          return true;
        } else {
          setError("Sikertelen regisztráció");
          return false;
        }
      } catch (err: any) {
        console.error("Register error:", err);
        const errorMessage =
          err instanceof ConvexError
            ? (typeof err.data === "string" ? err.data : (err.data as any)?.message || "Hiba történt")
            : err.message || "Hiba történt a regisztráció során";
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [registerAction]
  );

  const updateProfile = useCallback(
    async (userData: Partial<User> | FormData): Promise<boolean> => {
      if (!user) return false;

      setLoading(true);
      setError(null);

      try {
        let updateData: any = {};

        if (userData instanceof FormData) {
          userData.forEach((value, key) => {
            updateData[key] = value;
          });
        } else {
          updateData = { ...userData };
        }

        await updateProfileFullAction({
          sessionToken: sessionToken ?? undefined,
          username: updateData.username || undefined,
          email: updateData.email || undefined,
          profileImageUrl: updateData.profileImageUrl || undefined,
          oldPassword: updateData.currentPassword || undefined,
          newPassword: updateData.password || undefined,
        });

        // Profilkép: Convex storage-ba töltjük, majd a token felhasználójára állítjuk
        const avatar = updateData.avatar;
        if (avatar && typeof avatar === "object" && "size" in avatar) {
          const uploadUrl = await generateProfileImageUploadUrl({
            sessionToken: sessionToken ?? undefined,
          });
          const uploadResponse = await fetch(uploadUrl, {
            method: "POST",
            headers: { "Content-Type": (avatar as Blob).type || "application/octet-stream" },
            body: avatar as Blob,
          });
          if (!uploadResponse.ok) {
            throw new Error("A profilkép feltöltése sikertelen");
          }
          const { storageId } = await uploadResponse.json();
          await setProfileImageMutation({ sessionToken: sessionToken ?? undefined, storageId });
        } else if (updateData.removeAvatar === "true" || updateData.removeAvatar === true) {
          await setProfileImageMutation({ sessionToken: sessionToken ?? undefined, storageId: null });
        }

        await new Promise((resolve) => setTimeout(resolve, 500));
        return true;
      } catch (err: any) {
        console.error("Update profile error:", err);
        const errorMessage =
          err instanceof ConvexError
            ? (typeof err.data === "string" ? err.data : (err.data as any)?.message || "Hiba történt")
            : err.message || "Hiba történt a profil frissítése során";
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [user, sessionToken, updateProfileFullAction, generateProfileImageUploadUrl, setProfileImageMutation]
  );

  const value: AuthContextType = {
    user,
    sessionToken,
    loading,
    error,
    isAdmin: user?.role === "admin",
    login,
    logout,
    register,
    updateProfile,
    checkAuth,
    clearError,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
